import { Component, OnInit, ChangeDetectionStrategy, inject, DestroyRef, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { tap, catchError, first } from 'rxjs/operators';
import { of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { FeedbackService } from '@shared/services/api/feedback.service';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-learning-feedback-form',
  templateUrl: './learning-feedback-form.component.html',
  styleUrl: './learning-feedback-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LearningFeedbackFormComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _formBuilder = inject(FormBuilder);
  private _feedbackService = inject(FeedbackService);
  private _themeService = inject(ThemeService);
  private _location = inject(Location);
  private _route = inject(ActivatedRoute);
  public translateService = inject(TranslateService);

  form!: FormGroup;
  isLight = true;
  isLoading = false;
  errorMessage = '';
  learningId = '';

  showSuccess = false;
  showError = false;
  saveSuccess = false;
  saveError = false;

  constructor() {
    /* noop */
  }

  ngOnInit(): void {
    this.initializeForm();

    this._route.params.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params: any) => {
      this.learningId = params['id'] || '';
      this._cdr.markForCheck();
    });

    this._themeService.isLight$.pipe(first(), takeUntilDestroyed(this._destroyRef)).subscribe((isLight) => {
      this.isLight = isLight;
      this._cdr.markForCheck();
    });
  }

  private initializeForm(): void {
    this.form = this._formBuilder.group({
      content: ['', [Validators.required, Validators.minLength(10)]],
      rating: [0, [Validators.required, Validators.min(1), Validators.max(5)]]
    });
  }

  getYear() {
    return new Date().getFullYear();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    if (!this.learningId) {
      this.showError = true;
      this.saveError = true;
      this.errorMessage = this.translateService.instant('FEEDBACK_FORM.ERROR_NO_LEARNING_ID');
      return;
    }

    this.isLoading = true;
    this.resetStateFlags();

    const feedbackData = {
      content: this.form.value.content,
      rating: this.form.value.rating,
      learningId: this.learningId
    };

    this._feedbackService
      .submitFeedback(feedbackData)
      .pipe(
        first(),
        tap((response: any) => {
          this.isLoading = false;

          if (response && (response instanceof Error || response.error)) {
            this.showError = true;
            this.saveError = true;
            this.saveSuccess = false;
            this.errorMessage = this.extractErrorMessage(response);
            this._cdr.markForCheck();
            return;
          }

          this.showSuccess = true;
          this.saveSuccess = true;
          this.saveError = false;
          this.form.reset();
          this._cdr.markForCheck();
        }),
        catchError((error) => {
          this.isLoading = false;
          this.showError = true;
          this.saveError = true;
          this.errorMessage = this.extractErrorMessage(error?.error || error);
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  goBack(): void {
    this._location.back();
  }

  tryAgain(): void {
    this.saveError = false;
    this.showError = false;
    this._cdr.markForCheck();
  }

  private resetStateFlags(): void {
    this.showSuccess = false;
    this.showError = false;
    this.saveSuccess = false;
    this.saveError = false;
    this.errorMessage = '';
  }

  private extractErrorMessage(response: any): string {
    if (!response) {
      return this.translateService.instant('FEEDBACK_FORM.ERROR_UNKNOWN');
    }

    if (response.message) {
      return response.message;
    }

    if (response.validation && response.validation.fields && response.validation.fields.length > 0) {
      const validationErrors = response.validation.fields
        .map((field: any) => `${field.field}: ${field.error}`)
        .join('\n');

      return `Validation errors:\n${validationErrors}`;
    }

    if (response.error && typeof response.error === 'string') {
      return response.error;
    }

    return this.translateService.instant('FEEDBACK_FORM.ERROR_SAVING');
  }
}
